import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/** Shared shell for the monitoring dashboard: sidebar (Dashboard · Facilitators)
 *  on desktop, a top nav on mobile, and log out. Kept separate from the /admin
 *  DB browser — this surface has its own monitor credentials. */
const MonitorLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const nav = [
    { label: 'Dashboard', path: '/monitor' },
    { label: 'Facilitators', path: '/monitor/facilitators' },
    { label: 'Vacation', path: '/monitor/vacation' },
  ];
  const isActive = (p: string) =>
    p === '/monitor' ? pathname === '/monitor' : pathname.startsWith(p);

  const logout = () => {
    localStorage.removeItem('monitorToken');
    navigate('/monitor/login', { replace: true });
  };

  return (
    // Full-height shell: the sidebar stays fixed, only the content column scrolls.
    <div className="flex h-screen overflow-hidden bg-[#F5F4FA]">
      {/* Sidebar (desktop) — fixed height, does not scroll with the content */}
      <aside className="hidden h-screen w-56 flex-none flex-col border-r border-[#E9E6F4] bg-white p-4 sm:flex">
        <div className="mb-6 flex items-center gap-2 px-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-lg text-white">✦</span>
          <div>
            <p className="text-sm font-bold text-slate-900">Curiosity</p>
            <p className="text-xs text-slate-500">Monitoring</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1">
          {nav.map((n) => (
            <button
              key={n.path}
              type="button"
              onClick={() => navigate(n.path)}
              className={`rounded-xl px-3 py-2 text-left text-sm font-semibold ${
                isActive(n.path) ? 'bg-violet-50 text-violet-700' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {n.label}
            </button>
          ))}
        </nav>
        {/* Log out — pinned to the bottom, clearly marked */}
        <button
          type="button"
          onClick={logout}
          className="mt-auto flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-100"
        >
          <span aria-hidden className="text-base leading-none">⏻</span>
          Log out
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top nav (mobile) */}
        <div className="flex items-center justify-between border-b border-[#E9E6F4] bg-white px-4 py-3 sm:hidden">
          <div className="flex gap-1">
            {nav.map((n) => (
              <button
                key={n.path}
                type="button"
                onClick={() => navigate(n.path)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                  isActive(n.path) ? 'bg-violet-50 text-violet-700' : 'text-slate-600'
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={logout} className="flex items-center gap-1 text-sm font-semibold text-rose-600">
            <span aria-hidden>⏻</span> Log out
          </button>
        </div>
        {/* Only this column scrolls */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default MonitorLayout;
